import React, { useEffect, useRef, useState } from 'react';
import api from '../api';
import RunsPicture, { fmtDay, fmtSpan } from './RunsPicture';

// Move a run and everything after it on its machine (the director, 21 Oct
// 2026: "click on EN-140, 15th Dec, and push all from this point to 4th Jan
// and keep the order as it is now"). Opened from a run's bar on the board
// (Move…), or from a machine's wrench, which starts at that machine's first
// run that can move.
//
// One window, two sections he can fold: to a date, and by working days or to
// another machine -- what the wrench did before, now under the same rules
// (backend utils/moveFrom.js): runs move whole, the order and the gaps stay,
// running and finished runs and the frozen days never move, earlier only into
// free days, another machine only onto its free days. How far back it can go
// is said before he chooses. Before anything is saved: the picture, every
// cut-off it misses, and what the engine finds where the runs land; a mould
// that would be in another press stops the save.
//
// anchor: { planId?, jobId? } -- the run clicked -- or { onMachineId }.

const TEAL   = '#14b8a6';
const AMBER  = '#f59e0b';
const RED    = '#ef4444';
const BLUE   = '#93c5fd';
const BORDER = '#2a3040';
const MUTED  = '#6b7280';
const DIM    = '#9ca3af';
const TEXT   = '#f0f2f5';
const CHIPS  = [1, 2, 3, 5, 7, 10, 14];

const plural = (n, w) => `${n} ${w}${n === 1 ? '' : 's'}`;
const field = { background: '#131920', border: `1px solid ${BORDER}`, borderRadius: 8, color: TEXT, padding: '7px 10px', fontSize: 13, outline: 'none', fontFamily: 'system-ui', colorScheme: 'dark' };

export default function MoveModal({ anchor, machines = [], toast, onClose, onDone }) {
  const who = { plan_id: anchor.planId || undefined, job_id: anchor.planId ? undefined : (anchor.jobId || undefined), on_machine_id: anchor.onMachineId || undefined };
  const whoKey = JSON.stringify(who);

  const [base, setBase]         = useState(null);     // what moves, before a target
  const [baseErr, setBaseErr]   = useState(null);
  const [openDate, setOpenDate] = useState(true);
  const [openDays, setOpenDays] = useState(false);
  const [date, setDate]         = useState('');
  const [way, setWay]           = useState('later');  // section 2: later | earlier | machine
  const [n, setN]               = useState(null);
  const [custom, setCustom]     = useState('');
  const [machineId, setMachineId] = useState('');
  const [target, setTarget]     = useState(null);     // the last one set: { kind: 'date', date } | { kind: 'days', by } | { kind: 'machine', id }
  const [plan, setPlan]         = useState(null);
  const [loading, setLoading]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [err, setErr]           = useState(null);
  const seq = useRef(0);

  useEffect(() => {
    api.post('/api/v1/schedule/move-from/preview', who)
      .then(r => setBase(r.data))
      .catch(e => setBaseErr(e.response?.data?.error || 'Could not read the board.'));
  }, [whoKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const bodyFor = (t) => (t.kind === 'date' ? { to_date: t.date } : t.kind === 'days' ? { days: t.by } : { machine_id: Number(t.id) });

  // Every change of where to: the picture again, a moment after the last key.
  useEffect(() => {
    setErr(null);
    if (!target) { setPlan(null); return undefined; }
    const mine = ++seq.current;
    setLoading(true);
    const t = setTimeout(() => {
      api.post('/api/v1/schedule/move-from/preview', { ...who, ...bodyFor(target) })
        .then(r => { if (mine === seq.current) setPlan(r.data); })
        .catch(e => { if (mine === seq.current) { setPlan(null); setErr(e.response?.data?.error || 'Could not work it out.'); } })
        .finally(() => { if (mine === seq.current) setLoading(false); });
    }, 250);
    return () => clearTimeout(t);
  }, [JSON.stringify(target), whoKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // One choice at a time: picking in one section clears the other, so what
  // is filled in is what the picture shows.
  function pickDate(v) {
    setDate(v);
    if (v) { setN(null); setCustom(''); setMachineId(''); }
    setTarget(v ? { kind: 'date', date: v } : null);
  }
  function pickDays(k, w = way) {
    setN(k);
    if (k) { setDate(''); setMachineId(''); }
    setTarget(k ? { kind: 'days', by: w === 'earlier' ? -k : k } : null);
  }
  function pickWay(w) {
    setWay(w);
    if (w === 'machine' && machineId) { setDate(''); setTarget({ kind: 'machine', id: machineId }); }
    else if (w !== 'machine' && n) { setDate(''); setTarget({ kind: 'days', by: w === 'earlier' ? -n : n }); }
    else if (target?.kind !== 'date') setTarget(null);   // a date picked above stays what is shown
  }
  function pickMachine(v) {
    setMachineId(v);
    if (v) setDate('');
    setTarget(v ? { kind: 'machine', id: v } : null);
  }
  function takeEarliest(d) {
    setOpenDate(true);
    pickDate(d);
  }

  function save() {
    if (!plan || !plan.ok || !target) return;
    setSaving(true);
    setErr(null);
    api.post('/api/v1/schedule/move-from', { ...who, ...bodyFor(target), expect_sig: plan.sig })
      .then(r => {
        toast.success(`Moved — ${r.data.says}`, 8000);
        onDone(r.data);
      })
      .catch(e => {
        const d = e.response?.data;
        if (d?.plan) setPlan(d.plan);
        setErr(d?.error || 'Nothing was moved.');
        setSaving(false);
      });
  }

  const here = base?.machine;
  const backMax = base?.back_max || 0;
  const others = machines.filter(m => here && m.id !== here.id && m.status !== 'archived' && m.status !== 'decommissioned');
  const nameOf = (m) => m.display_name || m.name;
  const ready = plan && !plan.preview_only && !plan.refused;
  const active = target?.kind === 'date' ? 'date' : target ? 'days' : null;

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.62)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1300, padding: '4vh 16px', overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()} role="dialog" aria-label="Move runs"
        style={{ background: '#1a2030', border: `1px solid ${BORDER}`, borderRadius: 16, width: '100%', maxWidth: 820, padding: '18px 20px', fontFamily: 'system-ui', color: TEXT }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800 }}>Move{here ? ` · ${here.name}` : ''}</div>
            {base?.start && (
              <div style={{ fontSize: 12.5, color: DIM, marginTop: 4, lineHeight: 1.5 }}>
                <b style={{ color: TEXT }}>{base.start.name}</b> ({fmtSpan(base.start.from, base.start.to)}) and everything after it on {here.name}:
                {' '}{plural(base.moving.length, 'run')}, to {fmtDay(base.last_day)}. The order stays as it is.
              </div>
            )}
          </div>
          <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', color: MUTED, cursor: 'pointer', fontSize: 18 }}>✕</button>
        </div>

        {baseErr && <div style={{ color: '#fca5a5', fontSize: 13, marginTop: 12 }}>{baseErr}</div>}
        {!base && !baseErr && <div style={{ color: MUTED, fontSize: 13, marginTop: 12 }}>Reading the machine…</div>}
        {base?.start_note && (
          <div style={{ marginTop: 10, fontSize: 12.5, color: BLUE, background: 'rgba(147,197,253,0.08)', border: '1px solid rgba(147,197,253,0.25)', borderRadius: 8, padding: '7px 10px', lineHeight: 1.5 }}>
            <i className="fa-solid fa-circle-info" style={{ marginRight: 6 }} />{base.start_note}
          </div>
        )}
        {base?.start_caution && (
          <div style={{ marginTop: 10, fontSize: 12.5, color: '#fde68a', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.35)', borderRadius: 8, padding: '7px 10px', lineHeight: 1.5 }}>
            <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: 6, color: AMBER }} />{base.start_caution}
          </div>
        )}
        {base && !base.start && !base.start_note && <div style={{ color: DIM, fontSize: 13, marginTop: 12 }}>{base.says}</div>}

        {base?.start && (
          <>
            {/* ── To a date ── */}
            <Section title="To a date" sub={date && active === 'date' ? `It starts ${fmtDay(date)}` : 'The first run starts on the day you pick; the rest follow in order'}
              open={openDate} onToggle={() => setOpenDate(o => !o)} active={active === 'date'}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <label style={{ fontSize: 12, color: DIM }} htmlFor="move-to-date">It starts on</label>
                <input id="move-to-date" type="date" value={date} min={base.earliest || undefined} onChange={e => pickDate(e.target.value)} style={field} />
                {active === 'date' && plan?.by != null && !plan.refused && (
                  <span style={{ fontSize: 12, color: DIM }}>{plural(Math.abs(plan.by), 'working day')} {plan.by > 0 ? 'later' : 'earlier'}</span>
                )}
              </div>
              <Earlier base={base} />
            </Section>

            {/* ── By working days, or to another machine: the wrench ── */}
            <Section title="By working days, or to another machine" sub="Forward or back by a number of working days, or the same days on another machine"
              open={openDays} onToggle={() => setOpenDays(o => !o)} active={active === 'days'}>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                <Way on={way === 'later'} onClick={() => pickWay('later')} icon="fa-forward">Later</Way>
                <Way on={way === 'earlier'} onClick={() => pickWay('earlier')} icon="fa-backward" disabled={backMax === 0}
                  title={backMax === 0 ? base.earliest_says : undefined}>Earlier</Way>
                <Way on={way === 'machine'} onClick={() => pickWay('machine')} icon="fa-right-left">To another machine</Way>
              </div>
              {way !== 'machine' && (
                <>
                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center' }}>
                    {CHIPS.map(k => {
                      const off = way === 'earlier' && k > backMax;
                      return (
                        <button key={k} type="button" disabled={off} onClick={() => { setCustom(''); pickDays(k); }}
                          title={off ? base.earliest_says : `${plural(k, 'working day')} ${way}`}
                          style={{ minWidth: 38, padding: '6px 0', borderRadius: 7, fontSize: 12.5, fontWeight: 700, cursor: off ? 'not-allowed' : 'pointer',
                                   background: n === k && !custom ? TEAL : '#131920', color: n === k && !custom ? '#0f172a' : off ? '#4b5563' : DIM,
                                   border: `1px solid ${n === k && !custom ? TEAL : BORDER}`, opacity: off ? 0.5 : 1 }}>{k}</button>
                      );
                    })}
                    <input type="number" min="1" max={way === 'earlier' ? backMax || 1 : 250} placeholder="other" value={custom}
                      onChange={e => { setCustom(e.target.value); const k = Math.trunc(Number(e.target.value)); pickDays(k > 0 ? k : null); }}
                      style={{ ...field, width: 74, padding: '5px 8px' }} aria-label="Other number of working days" />
                    <span style={{ fontSize: 12, color: MUTED }}>working days {way}</span>
                  </div>
                  {way === 'earlier' && <Earlier base={base} />}
                </>
              )}
              {way === 'machine' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <select value={machineId} onChange={e => pickMachine(e.target.value)} style={{ ...field, minWidth: 220 }}>
                    <option value="">Pick a machine…</option>
                    {others.map(m => <option key={m.id} value={m.id}>{nameOf(m)}</option>)}
                  </select>
                  <span style={{ fontSize: 12, color: MUTED, lineHeight: 1.5 }}>The same days there, only where that machine is free and working.</span>
                </div>
              )}
            </Section>

            {/* ── What it does ── */}
            {loading && <div style={{ fontSize: 12.5, color: MUTED, marginTop: 14 }}>Working it out…</div>}
            {!loading && plan?.refused && (
              <div style={{ marginTop: 14, fontSize: 13, color: '#fde68a', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.35)', borderRadius: 10, padding: '10px 12px', lineHeight: 1.55 }}>
                {plan.says}
                {(plan.ways || []).length > 0 && (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                    {plan.ways.map((w, i) => (typeof w === 'string'
                      ? <div key={i} style={{ fontSize: 12.5, color: DIM, width: '100%' }}>· {w}</div>
                      : <button key={i} type="button" onClick={() => takeEarliest(w.date)}
                          style={{ background: `${TEAL}1f`, border: `1px solid ${TEAL}88`, color: TEAL, borderRadius: 8, padding: '6px 12px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>{w.says}</button>))}
                  </div>
                )}
              </div>
            )}
            {!loading && ready && <Outcome plan={plan} />}
            {!plan && !loading && !err && (
              <div style={{ marginTop: 14 }}>
                {(base.lanes || []).map(l => <RunsPicture key={l.machine_id} lane={l} choice="leave" />)}
              </div>
            )}

            {err && <div style={{ color: '#fca5a5', fontSize: 12.5, marginTop: 12 }}>{err}</div>}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center', marginTop: 16, flexWrap: 'wrap' }}>
              {ready && !plan.ok && <span style={{ fontSize: 12, color: '#fca5a5', marginRight: 'auto' }}>Not saved like this — see the red note above.</span>}
              <button onClick={onClose} style={{ background: 'none', border: '1px solid #374151', borderRadius: 8, color: DIM, padding: '8px 16px', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button onClick={save} disabled={!ready || !plan.ok || saving || loading}
                style={{ background: ready && plan.ok ? TEAL : '#1f2937', border: 'none', borderRadius: 8, color: ready && plan.ok ? '#0f172a' : '#4b5563',
                         padding: '8px 18px', cursor: ready && plan.ok && !saving ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 800 }}>
                {saving ? 'Moving…' : ready ? `Move ${plural(plan.shifted.length, 'run')}` : 'Move'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// How far back it can go, said before anything is chosen (21 Oct 2026: "this
// only works if there are no jobs scheduled before").
function Earlier({ base }) {
  const can = base.can_earlier;
  return (
    <div style={{ fontSize: 12, color: can ? DIM : MUTED, marginTop: 8, lineHeight: 1.5 }}>
      <i className={`fa-solid ${can ? 'fa-backward' : 'fa-ban'}`} style={{ marginRight: 6, fontSize: 10.5 }} />
      {base.earliest_says} {can ? 'Earlier only moves into free days: nothing else is pushed out of the way. ' : ''}Later: any day.
    </div>
  );
}

function Section({ title, sub, open, onToggle, active, children }) {
  return (
    <div style={{ marginTop: 12, border: `1px solid ${active ? `${TEAL}88` : BORDER}`, borderRadius: 10, background: active ? 'rgba(20,184,166,0.04)' : '#161b26' }}>
      <button type="button" onClick={onToggle} aria-expanded={open}
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, background: 'none', border: 'none', color: TEXT, padding: '10px 12px', cursor: 'pointer', textAlign: 'left', fontFamily: 'system-ui' }}>
        <i className={`fa-solid fa-chevron-${open ? 'down' : 'right'}`} style={{ fontSize: 10, color: DIM, width: 10 }} />
        <span style={{ fontSize: 13.5, fontWeight: 800 }}>{title}</span>
        <span style={{ fontSize: 12, color: MUTED, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{sub}</span>
      </button>
      {open && <div style={{ padding: '2px 12px 12px 32px' }}>{children}</div>}
    </div>
  );
}

function Way({ on, onClick, icon, disabled, title, children }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} title={title}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8, fontSize: 12.5, fontWeight: 700,
               cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.45 : 1,
               background: on ? 'rgba(20,184,166,0.15)' : '#131920', border: `1px solid ${on ? TEAL : BORDER}`, color: on ? TEAL : DIM }}>
      <i className={`fa-solid ${icon}`} style={{ fontSize: 10.5 }} />{children}
    </button>
  );
}

// What the move does: the sentence, the picture, the runs one by one, and
// what it causes -- a stop first, then cut-offs, then the engine's warnings;
// its notes folded away.
function Outcome({ plan }) {
  const late = (plan.late || []).filter(l => !l.was_late);
  const stillLate = (plan.late || []).filter(l => l.was_late);
  const stops = (plan.findings || []).filter(f => f.stops);
  const warns = (plan.findings || []).filter(f => !f.stops && f.severity !== 'note');
  const notes = (plan.findings || []).filter(f => !f.stops && f.severity === 'note');
  const lateKeys = new Set(late.map(l => l.key));
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ fontSize: 13, color: '#d1d5db', lineHeight: 1.55, marginBottom: 10 }}>
        {plan.says}{plan.snapped_says ? <span style={{ color: BLUE }}> {plan.snapped_says}</span> : null}
      </div>

      {stops.length > 0 && (
        <Box tone={RED} icon="fa-circle-xmark" title={plan.stops_says}>
          {stops.map((f, i) => <div key={i}>{f.name}: {f.says} — {fmtDay(f.day)}.</div>)}
          <div style={{ color: DIM, marginTop: 4 }}>Pick another date or number of days, or move the other run first (click it on the board → Move…).</div>
        </Box>
      )}

      {(plan.lanes || []).map(l => (
        <div key={l.machine_id} style={{ marginBottom: 10 }}>
          {plan.lanes.length > 1 && <div style={{ fontSize: 11, fontWeight: 800, color: DIM, letterSpacing: '0.06em', textTransform: 'uppercase', margin: '4px 0 6px 54px' }}>{l.machine}</div>}
          <RunsPicture lane={l} choice="leave" />
        </div>
      ))}

      {late.length > 0 && (
        <Box tone={RED} icon="fa-triangle-exclamation" title={`${plural(late.length, 'run')} would miss ${late.length === 1 ? 'its' : 'their'} cut-off`}>
          {late.map(l => <div key={l.key}>{l.name}: ends {fmtDay(l.ends)}, {plural(l.late_by, 'working day')} after its cut-off ({fmtDay(l.deadline)}).</div>)}
        </Box>
      )}
      {warns.length > 0 && (
        <Box tone={AMBER} icon="fa-circle-exclamation" title="Worth a look where they land">
          {warns.map((f, i) => <div key={i}>{f.name}: {f.says} — {fmtDay(f.day)}.</div>)}
        </Box>
      )}
      {(plan.jumped || []).length > 0 && (
        <Box tone={BLUE} icon="fa-share" title="Stepped past what cannot move">
          {plan.jumped.map((j, i) => <div key={i}>{j.name} jumped past {j.past} — it starts {fmtDay(j.starts)}.</div>)}
        </Box>
      )}

      <details style={{ marginTop: 8 }}>
        <summary style={{ fontSize: 12, color: DIM, cursor: 'pointer' }}>Every run, from → to ({plan.shifted.length})</summary>
        <div style={{ maxHeight: 220, overflowY: 'auto', marginTop: 6, fontSize: 12, color: DIM, lineHeight: 1.6, fontVariantNumeric: 'tabular-nums' }}>
          {plan.shifted.map(s => (
            <div key={s.key} style={{ color: lateKeys.has(s.key) ? '#fca5a5' : DIM }}>
              {s.name}: {fmtSpan(s.from[0], s.from[1])} → <b style={{ color: lateKeys.has(s.key) ? '#fca5a5' : '#d1d5db' }}>{fmtSpan(s.to[0], s.to[1])}</b>
            </div>
          ))}
        </div>
      </details>
      {stillLate.length > 0 && (
        <div style={{ fontSize: 11.5, color: MUTED, marginTop: 6 }}>
          Already late before this move: {stillLate.map(l => l.name).join(', ')}.
        </div>
      )}
      {notes.length > 0 && (
        <details style={{ marginTop: 6 }}>
          <summary style={{ fontSize: 12, color: MUTED, cursor: 'pointer' }}>Also worth knowing ({notes.length})</summary>
          <div style={{ fontSize: 12, color: MUTED, lineHeight: 1.55, marginTop: 4 }}>
            {notes.map((f, i) => <div key={i}>{f.name}: {f.says}</div>)}
          </div>
        </details>
      )}
    </div>
  );
}

function Box({ tone, icon, title, children }) {
  return (
    <div style={{ margin: '8px 0', fontSize: 12.5, color: '#d1d5db', background: `${tone}12`, border: `1px solid ${tone}55`, borderRadius: 10, padding: '9px 12px', lineHeight: 1.55 }}>
      <div style={{ color: tone, fontWeight: 800, marginBottom: 3 }}><i className={`fa-solid ${icon}`} style={{ marginRight: 6 }} />{title}</div>
      {children}
    </div>
  );
}
